import { useTranscriptionsState } from '@modules/transcriptions/useTranscriptionsState';
import { useMemo } from 'react';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { VoiceChannelMember } from 'src/provider/VoiceChannel';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';

type Props = {
  member: VoiceChannelMember;
  connected: boolean;
  aloneMember: boolean;
};

export const useMemberVariables = ({
  member,
  connected,
  aloneMember,
}: Props) => {
  const { upSmallMobile } = useBreakpoints();

  const {
    room,
    myMeetId,
    connectionStatus,
    viewMode,
    activeMember,
    theaterMember,
  } = useLivekitRTCState();

  const { activeTranscriptionOwner } = useTranscriptionsState();

  // Local or Remote Participant according to meetId
  const participant = useMemo(() => {
    if (!room || !connected) return undefined;
    if (!member?.meetId || !member?.identity) return undefined;

    return member.meetId === myMeetId
      ? room.localParticipant
      : room.remoteParticipants.get(member.identity);
  }, [room, connected, member?.meetId, member?.identity, myMeetId]);

  // If member started transcription
  const startedByCurrentMember = activeTranscriptionOwner === member?.user?._id;

  // If member is speaking
  const isSpeaking = participant?.isSpeaking || false;

  // If member has camera enabled or sharing screen
  const hasVideo = member?.sharing || member?.camera || false;

  // If member is the pinned member (theaterMember)
  const memberTheater = !!theaterMember && theaterMember === member?.meetId;

  // If in list mode and member is the active speaker
  const memberListActive =
    viewMode === 'list' && !theaterMember && activeMember === member?.meetId;

  // If member is pinned or active speaker, or is alone in the room
  const theater =
    connected && (aloneMember || memberTheater || memberListActive);

  // if member is not connected
  const notConnected =
    connectionStatus === 'connecting' || member?.status !== 'connected';

  // If is not in theater mode or list view
  const noTheater = !theater && (!!theaterMember || viewMode === 'list');

  // If has some pinned member or in list view with active speaker
  const hasTheaterOrActiveMember =
    !!theaterMember || (viewMode === 'list' && !!activeMember);

  // Is small card if not connected, mobile or not theater and has some pinned or active member
  const isSmall =
    !connected || !upSmallMobile || (!theater && hasTheaterOrActiveMember);

  return {
    theater,
    isSmall,
    hasVideo,
    noTheater,
    isSpeaking,
    notConnected,
    startedByCurrentMember,
  };
};
